import FullScreenLayout from "../layouts/full-screen-layout";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import logo from "../resources/logo.svg";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AccountService from "../services/AccountService";
import { useNavigate, useLocation } from "react-router-dom";

const isValidPassword = (value) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(value);

export default function ResetPassword() {
  const redirect = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const { id, token } = location.state ?? {};

  useEffect(() => {
    if (!id || !token) {
      redirect("/forgot-password", { replace: true });
    }
  }, [id, token, redirect]);

  const handlePasswordChange = (value) => {
    setPassword(value);
    setApiError("");
    if (value && !isValidPassword(value)) {
      setPasswordError("Must be 8+ characters with uppercase, lowercase, and a number.");
    } else {
      setPasswordError("");
    }
    if (password2 && value !== password2) {
      setConfirmError("Passwords do not match.");
    } else {
      setConfirmError("");
    }
  };

  const handleConfirmChange = (value) => {
    setPassword2(value);
    setApiError("");
    if (value && value !== password) {
      setConfirmError("Passwords do not match.");
    } else {
      setConfirmError("");
    }
  };

  const isSubmitDisabled = !isValidPassword(password) || password !== password2 || password2 === "" || loading;

  const onClick = () => {
    if (isSubmitDisabled) return;
    setLoading(true);
    setApiError("");
    AccountService.resetPassword(id, token, password, password2)
      .then(() => redirect("/login", { replace: true }))
      .catch(() => setApiError("Failed to reset password. The link may have expired."))
      .finally(() => setLoading(false));
  };

  const onPressEnter = (e) => {
    if (e.key === "Enter") onClick();
  };

  if (!id || !token) return null;

  return (
    <FullScreenLayout>
      <div className="flex-auto mt-8 shadow-none border-noround w-26rem bg-white px-4 py-4 border-1 border-300">
        <div className="flex flex-column align-items-center justify-content-center border-round m-2">
          <img src={logo} alt="logo" width={120} className="flex justify-content-center" />
          <p className="text-color font-bold mt-3">Reset Your Password</p>
        </div>

        <div className="field">
          <label htmlFor="password">New Password</label>
          <Password
            id="password"
            value={password}
            placeholder="Enter new password"
            onChange={(e) => handlePasswordChange(e.target.value)}
            toggleMask
            feedback={false}
            className="w-full"
            inputClassName="text-base text-color surface-overlay p-2 border-1 border-solid surface-border border-round appearance-none outline-none focus:border-primary w-full"
          />
          {passwordError && <small className="text-red-500">{passwordError}</small>}
        </div>

        <div className="field mb-4">
          <label htmlFor="password2">Confirm Password</label>
          <Password
            id="password2"
            value={password2}
            placeholder="Confirm new password"
            onChange={(e) => handleConfirmChange(e.target.value)}
            onKeyUpCapture={onPressEnter}
            toggleMask
            feedback={false}
            className="w-full"
            inputClassName="text-base text-color surface-overlay p-2 border-1 border-solid surface-border border-round appearance-none outline-none focus:border-primary w-full"
          />
          {confirmError && <small className="text-red-500">{confirmError}</small>}
        </div>

        {apiError && <p className="text-red-500 text-sm mt-0 mb-3">{apiError}</p>}

        <Button
          label="Reset Password"
          className="w-full"
          disabled={isSubmitDisabled}
          loading={loading}
          onClick={onClick}
        />

        <Link to="/login" className="flex text-blue-500 no-underline justify-content-center w-full mt-4">
          Return to log in
        </Link>
      </div>
    </FullScreenLayout>
  );
}
